import React, { memo } from "react";
import { parse, format } from "mathjs";

// Enhanced math renderer with mathjs support
function MathRenderer({ text }) {
    // Handle LaTeX-style notation and convert to proper HTML
    let result = text
        // Handle superscripts: x^2, x^{123}, x^(3), etc.
        .replace(/\^{([^}]+)}/g, "<sup>$1</sup>")
        .replace(/\^\(([^)]+)\)/g, "<sup>$1</sup>")
        .replace(/\^([A-Za-z0-9]+)/g, "<sup>$1</sup>")
        // Handle subscripts: x_2, x_{123}, x_(3), etc.
        .replace(/_{([^}]+)}/g, "<sub>$1</sub>")
        .replace(/_\(([^)]+)\)/g, "<sub>$1</sub>")
        .replace(/_([A-Za-z0-9]+)/g, "<sub>$1</sub>")
        // Handle LaTeX commands for Greek letters
        .replace(/\\alpha/g, "α")
        .replace(/\\beta/g, "β")
        .replace(/\\gamma/g, "γ")
        .replace(/\\delta/g, "δ")
        .replace(/\\epsilon/g, "ε")
        .replace(/\\zeta/g, "ζ")
        .replace(/\\eta/g, "η")
        .replace(/\\theta/g, "θ")
        .replace(/\\iota/g, "ι")
        .replace(/\\kappa/g, "κ")
        .replace(/\\lambda/g, "λ")
        .replace(/\\mu/g, "μ")
        .replace(/\\nu/g, "ν")
        .replace(/\\xi/g, "ξ")
        .replace(/\\pi/g, "π")
        .replace(/\\rho/g, "ρ")
        .replace(/\\sigma/g, "σ")
        .replace(/\\tau/g, "τ")
        .replace(/\\upsilon/g, "υ")
        .replace(/\\phi/g, "φ")
        .replace(/\\chi/g, "χ")
        .replace(/\\psi/g, "ψ")
        .replace(/\\omega/g, "ω")
        // Capital Greek letters
        .replace(/\\Gamma/g, "Γ")
        .replace(/\\Delta/g, "Δ")
        .replace(/\\Theta/g, "Θ")
        .replace(/\\Lambda/g, "Λ")
        .replace(/\\Xi/g, "Ξ")
        .replace(/\\Pi/g, "Π")
        .replace(/\\Sigma/g, "Σ")
        .replace(/\\Phi/g, "Φ")
        .replace(/\\Psi/g, "Ψ")
        .replace(/\\Omega/g, "Ω")
        // Math symbols
        .replace(/\\cdot/g, "·")
        .replace(/\\times/g, "×")
        .replace(/\\div/g, "÷")
        .replace(/\\pm/g, "±")
        .replace(/\\mp/g, "∓")
        .replace(/\\nabla/g, "∇")
        .replace(/\\partial/g, "∂")
        .replace(/\\infty/g, "∞")
        .replace(/\\sum/g, "∑")
        .replace(/\\prod/g, "∏")
        .replace(/\\int/g, "∫")
        .replace(/\\neq/g, "≠")
        .replace(/\\leq/g, "≤")
        .replace(/\\geq/g, "≥")
        .replace(/\\le/g, "≤")
        .replace(/\\ge/g, "≥")
        .replace(/\\lt/g, "<")
        .replace(/\\gt/g, ">")
        .replace(/\\ll/g, "≪")
        .replace(/\\gg/g, "≫")
        .replace(/\\approx/g, "≈")
        .replace(/\\equiv/g, "≡")
        .replace(/\\propto/g, "∝")
        .replace(/\\sim/g, "∼")
        .replace(/\\simeq/g, "≃")
        .replace(/\\cong/g, "≅")
        .replace(/\\in/g, "∈")
        .replace(/\\notin/g, "∉")
        .replace(/\\subset/g, "⊂")
        .replace(/\\supset/g, "⊃")
        .replace(/\\subseteq/g, "⊆")
        .replace(/\\supseteq/g, "⊇")
        .replace(/\\cap/g, "∩")
        .replace(/\\cup/g, "∪")
        .replace(/\\emptyset/g, "∅")
        .replace(/\\forall/g, "∀")
        .replace(/\\exists/g, "∃")
        .replace(/\\neg/g, "¬")
        .replace(/\\land/g, "∧")
        .replace(/\\lor/g, "∨")
        .replace(/\\oplus/g, "⊕")
        .replace(/\\otimes/g, "⊗")
        // Handle unit-related symbols and common engineering notation
        .replace(/\\Omega/g, "Ω")
        .replace(/\\ohm/g, "Ω")
        .replace(/\\celsius/g, "°C")
        .replace(/\\fahrenheit/g, "°F")
        .replace(/\\kelvin/g, "K")
        .replace(/\\volt/g, "V")
        .replace(/\\amp/g, "A")
        .replace(/\\watt/g, "W")
        .replace(/\\joule/g, "J")
        .replace(/\\newton/g, "N")
        .replace(/\\pascal/g, "Pa")
        .replace(/\\hertz/g, "Hz")
        .replace(/\\meter/g, "m")
        .replace(/\\kilogram/g, "kg")
        .replace(/\\second/g, "s")
        // Handle arrows and other symbols
        .replace(/\\rightarrow/g, "→")
        .replace(/\\leftarrow/g, "←")
        .replace(/\\uparrow/g, "↑")
        .replace(/\\downarrow/g, "↓")
        .replace(/\\leftrightarrow/g, "↔")
        .replace(/\\Rightarrow/g, "⇒")
        .replace(/\\Leftarrow/g, "⇐")
        .replace(/\\Leftrightarrow/g, "⇔")
        // Handle additional math symbols
        .replace(/\\angle/g, "∠")
        .replace(/\\perp/g, "⊥")
        .replace(/\\parallel/g, "∥")
        .replace(/\\triangle/g, "△")
        .replace(/\\square/g, "□")
        .replace(/\\diamond/g, "◊")
        // Advanced mathematical operators and symbols
        .replace(/\\lim/g, "lim")
        .replace(/\\max/g, "max")
        .replace(/\\min/g, "min")
        .replace(/\\sup/g, "sup")
        .replace(/\\inf/g, "inf")
        .replace(/\\det/g, "det")
        .replace(/\\dim/g, "dim")
        .replace(/\\ker/g, "ker")
        .replace(/\\rank/g, "rank")
        .replace(/\\trace/g, "tr")
        .replace(/\\gcd/g, "gcd")
        .replace(/\\lcm/g, "lcm")
        .replace(/\\log/g, "log")
        .replace(/\\ln/g, "ln")
        .replace(/\\lg/g, "lg")
        .replace(/\\exp/g, "exp")
        .replace(/\\sin/g, "sin")
        .replace(/\\cos/g, "cos")
        .replace(/\\tan/g, "tan")
        .replace(/\\cot/g, "cot")
        .replace(/\\sec/g, "sec")
        .replace(/\\csc/g, "csc")
        .replace(/\\sinh/g, "sinh")
        .replace(/\\cosh/g, "cosh")
        .replace(/\\tanh/g, "tanh")
        .replace(/\\arcsin/g, "arcsin")
        .replace(/\\arccos/g, "arccos")
        .replace(/\\arctan/g, "arctan")
        // Calculus and analysis
        .replace(/\\oint/g, "∮")
        .replace(/\\iint/g, "∬")
        .replace(/\\iiint/g, "∭")
        .replace(/\\lim/g, "lim")
        .replace(/\\limsup/g, "lim sup")
        .replace(/\\liminf/g, "lim inf")
        // More comparison and relation symbols
        .replace(/\\prec/g, "≺")
        .replace(/\\succ/g, "≻")
        .replace(/\\preceq/g, "⪯")
        .replace(/\\succeq/g, "⪰")
        .replace(/\\nless/g, "≮")
        .replace(/\\ngtr/g, "≯")
        .replace(/\\nleq/g, "≰")
        .replace(/\\ngeq/g, "≱")
        .replace(/\\doteq/g, "≐")
        .replace(/\\fallingdotseq/g, "≒")
        .replace(/\\risingdotseq/g, "≓")
        // Binary operations
        .replace(/\\ast/g, "∗")
        .replace(/\\star/g, "⋆")
        .replace(/\\circ/g, "∘")
        .replace(/\\bullet/g, "∙")
        .replace(/\\bigcirc/g, "◯")
        .replace(/\\triangleleft/g, "◁")
        .replace(/\\triangleright/g, "▷")
        .replace(/\\bigtriangleup/g, "△")
        .replace(/\\bigtriangledown/g, "▽")
        .replace(/\\wedge/g, "∧")
        .replace(/\\vee/g, "∨")
        .replace(/\\cap/g, "∩")
        .replace(/\\cup/g, "∪")
        .replace(/\\uplus/g, "⊎")
        .replace(/\\sqcap/g, "⊓")
        .replace(/\\sqcup/g, "⊔")
        .replace(/\\amalg/g, "⨿")
        // More arrows
        .replace(/\\mapsto/g, "↦")
        .replace(/\\hookrightarrow/g, "↪")
        .replace(/\\hookleftarrow/g, "↩")
        .replace(/\\rightharpoonup/g, "⇀")
        .replace(/\\rightharpoondown/g, "⇁")
        .replace(/\\leftharpoonup/g, "↼")
        .replace(/\\leftharpoondown/g, "↽")
        .replace(/\\rightleftharpoons/g, "⇌")
        .replace(/\\twoheadrightarrow/g, "↠")
        .replace(/\\twoheadleftarrow/g, "↞")
        .replace(/\\longmapsto/g, "⟼")
        .replace(/\\longrightarrow/g, "⟶")
        .replace(/\\longleftarrow/g, "⟵")
        .replace(/\\longleftrightarrow/g, "⟷")
        .replace(/\\Longrightarrow/g, "⟹")
        .replace(/\\Longleftarrow/g, "⟸")
        .replace(/\\Longleftrightarrow/g, "⟺")
        // Miscellaneous symbols
        .replace(/\\hbar/g, "ℏ")
        .replace(/\\ell/g, "ℓ")
        .replace(/\\wp/g, "℘")
        .replace(/\\Re/g, "ℜ")
        .replace(/\\Im/g, "ℑ")
        .replace(/\\aleph/g, "ℵ")
        .replace(/\\beth/g, "ℶ")
        .replace(/\\gimel/g, "ℷ")
        .replace(/\\daleth/g, "ℸ")
        .replace(/\\imath/g, "ı")
        .replace(/\\jmath/g, "ȷ")
        // Geometry and topology
        .replace(/\\sphericalangle/g, "∢")
        .replace(/\\measuredangle/g, "∡")
        .replace(/\\because/g, "∵")
        .replace(/\\therefore/g, "∴")
        // Chemistry and physics symbols
        .replace(/\\rightleftharpoons/g, "⇌")
        .replace(/\\equilibrium/g, "⇌")
        // Additional Greek letters (extended)
        .replace(/\\digamma/g, "ϝ")
        .replace(/\\varkappa/g, "ϰ")
        .replace(/\\varpi/g, "ϖ")
        .replace(/\\varrho/g, "ϱ")
        .replace(/\\varsigma/g, "ς")
        .replace(/\\vartheta/g, "ϑ")
        .replace(/\\varUpsilon/g, "ϒ")
        .replace(/\\varphi/g, "φ")
        .replace(/\\varepsilon/g, "ε")
        // More units and constants
        .replace(/\\angstrom/g, "Å")
        .replace(/\\micro/g, "µ")
        .replace(/\\nano/g, "n")
        .replace(/\\pico/g, "p")
        .replace(/\\femto/g, "f")
        .replace(/\\atto/g, "a")
        .replace(/\\kilo/g, "k")
        .replace(/\\mega/g, "M")
        .replace(/\\giga/g, "G")
        .replace(/\\tera/g, "T")
        .replace(/\\peta/g, "P")
        .replace(/\\exa/g, "E")
        // Handle fractions: \frac{a}{b} → proper fraction display
        .replace(
            /\\frac{([^}]+)}{([^}]+)}/g,
            '<span style="display:inline-block; text-align:center; vertical-align:middle; margin:0 2px;"><span style="display:block; border-bottom:1px solid; padding:0 2px; font-size:0.9em;">$1</span><span style="display:block; padding:0 2px; font-size:0.9em;">$2</span></span>'
        )
        // Handle square roots
        .replace(/\\sqrt{([^}]+)}/g, "√($1)")
        .replace(
            /\\sqrt\[([^\]]+)\]{([^}]+)}/g,
            '<sup style="font-size:0.7em;">$1</sup>√($2)'
        )
        // Handle common operations
        .replace(/\*/g, "×")
        .replace(/\\\\/g, "<br/>")
        // Handle degree symbol
        .replace(/\\degree/g, "°")
        .replace(/\^\{\\circ\}/g, "°")
        // Handle text styling in math mode
        .replace(/\\text\{([^}]+)\}/g, "$1")
        .replace(/\\mathrm\{([^}]+)\}/g, "$1")
        .replace(/\\mathbf\{([^}]+)\}/g, "<strong>$1</strong>")
        .replace(/\\mathit\{([^}]+)\}/g, "<em>$1</em>")
        .replace(
            /\\mathsf\{([^}]+)\}/g,
            '<span style="font-family: sans-serif;">$1</span>'
        )
        .replace(
            /\\mathtt\{([^}]+)\}/g,
            '<span style="font-family: monospace;">$1</span>'
        )
        .replace(
            /\\mathcal\{([^}]+)\}/g,
            '<span style="font-family: cursive;">$1</span>'
        )
        // Handle spacing commands
        .replace(/\\quad/g, "    ")
        .replace(/\\qquad/g, "        ")
        .replace(/\\,/g, " ")
        .replace(/\\;/g, "  ")
        .replace(/\\!/g, "")
        // Handle common physics/engineering expressions
        .replace(
            /\\vec\{([^}]+)\}/g,
            '<span style="position: relative;">$1<span style="position: absolute; top: -0.5em; left: 0; right: 0; text-align: center; font-size: 0.8em;">→</span></span>'
        )
        .replace(
            /\\hat\{([^}]+)\}/g,
            '<span style="position: relative;">$1<span style="position: absolute; top: -0.5em; left: 0; right: 0; text-align: center; font-size: 0.8em;">^</span></span>'
        )
        .replace(
            /\\bar\{([^}]+)\}/g,
            '<span style="position: relative;">$1<span style="position: absolute; top: -0.3em; left: 0; right: 0; text-align: center; font-size: 0.8em; border-top: 1px solid;">‾</span></span>'
        )
        .replace(
            /\\dot\{([^}]+)\}/g,
            '<span style="position: relative;">$1<span style="position: absolute; top: -0.5em; left: 0; right: 0; text-align: center; font-size: 0.8em;">·</span></span>'
        )
        .replace(
            /\\ddot\{([^}]+)\}/g,
            '<span style="position: relative;">$1<span style="position: absolute; top: -0.5em; left: 0; right: 0; text-align: center; font-size: 0.8em;">¨</span></span>'
        )
        // Handle matrix notation (simplified)
        .replace(/\\begin\{matrix\}([^}]+)\\end\{matrix\}/g, "[$1]")
        .replace(/\\begin\{pmatrix\}([^}]+)\\end\{pmatrix\}/g, "($1)")
        .replace(/\\begin\{bmatrix\}([^}]+)\\end\{bmatrix\}/g, "[$1]")
        .replace(/\\begin\{vmatrix\}([^}]+)\\end\{vmatrix\}/g, "|$1|")
        // Handle chemical formulas and subscripts
        .replace(/([A-Za-z]+)_\{([^}]+)\}/g, "$1<sub>$2</sub>")
        .replace(/([A-Za-z]+)_([0-9]+)/g, "$1<sub>$2</sub>")
        // Handle common abbreviations
        .replace(/\\etc/g, "etc.")
        .replace(/\\ie/g, "i.e.")
        .replace(/\\eg/g, "e.g.")
        .replace(/\\cf/g, "cf.")
        // Handle currencies and special characters
        .replace(/\\$/g, "$")
        .replace(/\\&/g, "&")
        .replace(/\\%/g, "%")
        .replace(/\\#/g, "#")
        .replace(/\\_/g, "_")
        .replace(/\\\{/g, "{")
        .replace(/\\\}/g, "}");

    return (
        <span
            dangerouslySetInnerHTML={{ __html: result }}
            style={{
                display: "inline",
                fontFamily: "inherit",
                fontSize: "inherit",
                lineHeight: "inherit",
                verticalAlign: "baseline",
            }}
        />
    );
}

// Improved tokenizer for better math detection
function tokenize(text) {
    // Enhanced pattern to catch more math expressions including short LaTeX commands and parentheses
    const mathPattern =
        /(\\\w+(?:\{[^}]*\})*|\\[a-zA-Z]+|[A-Za-z0-9]+[\^_]\{[^}]+\}|[A-Za-z0-9]+[\^_]\([^)]+\)|[A-Za-z0-9]+[\^_][A-Za-z0-9]+|[×·÷±∇∂∞∑∏∫≠≤≥≪≫≈≡∝∼≃≅∈∉⊂⊃⊆⊇∩∪∅∀∃¬∧∨⊕⊗°Ω→←↑↓↔⇒⇐⇔∠⊥∥△□◊∮∬∭≺≻⪯⪰≮≯≰≱≐≒≓∗⋆∘∙◯◁▷▽⊎⊓⊔⨿↦↪↩⇀⇁↼↽⇌↠↞⟼⟶⟵⟷⟹⟸⟺ℏℓ℘ℜℑℵℶℷℸıȷ∢∡∵∴ϝϰϖϱςϑϒφεÅµ]|√\([^)]+\))/g;

    const tokens = [];
    let lastIndex = 0;
    let match;

    while ((match = mathPattern.exec(text)) !== null) {
        // Add text before match
        if (match.index > lastIndex) {
            tokens.push({
                type: "text",
                content: text.slice(lastIndex, match.index),
            });
        }

        // Add math token
        tokens.push({
            type: "math",
            content: match[0],
        });

        lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
        tokens.push({
            type: "text",
            content: text.slice(lastIndex),
        });
    }

    return tokens;
}

// Enhanced math content detection
function hasMathContent(text) {
    return /[\^_]|\\\w+|[×·÷±∇∂∞∑∏∫≠≤≥≪≫≈≡∝∼≃≅∈∉⊂⊃⊆⊇∩∪∅∀∃¬∧∨⊕⊗°Ω→←↑↓↔⇒⇐⇔∠⊥∥△□◊∮∬∭≺≻⪯⪰≮≯≰≱≐≒≓∗⋆∘∙◯◁▷▽⊎⊓⊔⨿↦↪↩⇀⇁↼↽⇌↠↞⟼⟶⟵⟷⟹⟸⟺ℏℓ℘ℜℑℵℶℷℸıȷ∢∡∵∴ϝϰϖϱςϑϒφεÅµ]|√\(|\\frac|\\sqrt|\^\(|_\(/.test(
        text
    );
}

// Try to evaluate simple mathematical expressions with mathjs
function tryEvaluateExpression(text) {
    try {
        // Clean text for mathjs evaluation
        const cleanText = text
            .replace(/[×·]/g, "*")
            .replace(/÷/g, "/")
            .replace(/\^/g, "^")
            .replace(/[^\d+\-*/^(). ]/g, ""); // Remove non-math characters

        // Only try to evaluate if it looks like a simple math expression
        if (/^[0-9+\-*/^(). ]+$/.test(cleanText) && cleanText.trim()) {
            const result = parse(cleanText).evaluate();
            if (
                typeof result === "number" &&
                !isNaN(result) &&
                isFinite(result)
            ) {
                return ` = ${format(result, { precision: 6 })}`;
            }
        }
    } catch (e) {
        // Ignore evaluation errors
    }
    return "";
}

// Main component
export default memo(function EnhancedLatexRenderer({ text }) {
    if (!text || typeof text !== "string") {
        return <span>{text || ""}</span>;
    }

    // If no math content, return as-is
    if (!hasMathContent(text)) {
        return <span>{text}</span>;
    }

    // For simple expressions, try to show evaluation
    const evaluation = tryEvaluateExpression(text);

    // Process mixed content
    const tokens = tokenize(text);

    return (
        <span style={{ display: "inline" }}>
            {tokens.map((token, index) => {
                if (token.type === "math") {
                    return <MathRenderer key={index} text={token.content} />;
                } else {
                    return <span key={index}>{token.content}</span>;
                }
            })}
            {evaluation && (
                <span
                    style={{
                        color: "#888",
                        fontSize: "0.9em",
                        marginLeft: "8px",
                    }}
                >
                    {evaluation}
                </span>
            )}
        </span>
    );
});
